import { jsonStringToFlatObject } from "@phoenix/utils/jsonUtils";
import { createNumberFormatter } from "@phoenix/utils/numberFormatUtils";

/**
 * Formats a TTFT value cleanly (e.g., "450 ms", "1.2 s", "--").
 * Rules:
 *   < 1000 ms -> "450 ms"
 *   >= 1000 ms -> "1.2 s"
 *   missing/invalid -> "--"
 */
export function fmtTTFT(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || isNaN(ms)) return "--";
  if (ms < 1000) {
    return `${Math.round(ms)} ms`;
  }
  return `${(ms / 1000).toFixed(1)} s`;
}

/**
 * Helper to extract an explicit TTFT metric or first-token timestamp from attributes or metadata.
 */
function extractExplicitTTFTFromObj(
  obj: any,
  spanStartTime?: string | number | Date | null
): number | null {
  if (!obj) return null;
  const parsed = typeof obj === "string" ? jsonStringToFlatObject(obj) : obj;
  if (!parsed || typeof parsed !== "object") return null;

  // 1. Check explicit TTFT / first-chunk metric attributes
  const keysInMs = [
    "gen_ai.response.time_to_first_chunk",
    "gen_ai.response.time_to_first_token",
    "gen_ai.client.token.first",
    "llm.ttft_ms",
    "llm.time_to_first_token_ms",
    "ttft_ms",
    "ttft",
    "first_token_ms",
    "evalight.ttft_ms",
    "metadata.ttft_ms",
    "llm.latency.first_token_ms",
    "llm.first_token_latency_ms",
    "time_to_first_token",
  ];

  for (const key of keysInMs) {
    if (
      parsed[key] !== undefined &&
      parsed[key] !== null &&
      parsed[key] !== ""
    ) {
      const num = parseFloat(parsed[key]);
      if (!isNaN(num) && num >= 0) {
        // Convert fractional seconds to ms if metric is in seconds (e.g. gen_ai.response.time_to_first_chunk = 0.45)
        if (key.startsWith("gen_ai.") && num < 60 && !key.endsWith("_ms")) {
          return num * 1000;
        }
        return num;
      }
    }
  }

  // 2. Check explicit first-token / first-chunk timestamp attributes
  const timeKeys = [
    "gen_ai.response.first_token_timestamp",
    "gen_ai.response.first_chunk_timestamp",
    "llm.first_token_timestamp",
    "llm.first_token_time",
    "first_token_time",
    "first_token_timestamp",
    "evalight.first_token_time",
  ];

  if (spanStartTime) {
    const tStart = new Date(spanStartTime).getTime();
    if (!isNaN(tStart)) {
      for (const key of timeKeys) {
        if (
          parsed[key] !== undefined &&
          parsed[key] !== null &&
          parsed[key] !== ""
        ) {
          const tFirst = new Date(parsed[key]).getTime();
          if (!isNaN(tFirst) && tFirst >= tStart) {
            return tFirst - tStart;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Calculates LLM TTFT (LLM span start -> first output token).
 * Returns null if no reliable first-token telemetry is found.
 */
export function calculateLLMTTFTMs(span: any): number | null {
  if (!span) return null;
  return (
    extractExplicitTTFTFromObj(span.attributes, span.startTime) ??
    extractExplicitTTFTFromObj(span.metadata, span.startTime)
  );
}

/**
 * Calculates Request / Root TTFT (root request start -> first LLM output token).
 * Used for the table's TTFT column.
 *
 * Rules:
 *  1. Priority 1: Direct explicit TTFT attributes on root span.
 *  2. Priority 2: Direct explicit first-token timestamp on root span minus root start time.
 *  3. Priority 3: For nested agent/chain traces, find the earliest descendant LLM span with explicit first-token timing:
 *        Root TTFT = (child.startTime - root.startTime) + child_LLM_TTFT
 *  4. Priority 4: Return null (--) if no reliable first-token telemetry exists.
 *  5. NO fallback to 25% total latency. NO assuming child start time = first token time without telemetry.
 */
export function calculateTTFTMs(span: any): number | null {
  if (!span) return null;

  // 1 & 2. Direct explicit TTFT or timestamp on root span attributes / metadata
  const rootDirectTTFT =
    extractExplicitTTFTFromObj(span.attributes, span.startTime) ??
    extractExplicitTTFTFromObj(span.metadata, span.startTime);

  if (rootDirectTTFT !== null) {
    return rootDirectTTFT;
  }

  const rootStartTime = span.startTime ? new Date(span.startTime).getTime() : null;

  // 3. Check descendant spans for nested agent/chain traces
  if (span.descendants?.edges && span.descendants.edges.length > 0) {
    let earliestRootTTFT: number | null = null;
    let minDiff = Infinity;

    for (const edge of span.descendants.edges) {
      const child = edge.node;
      if (!child) continue;

      // Extract explicit TTFT from child span
      const childTTFT =
        extractExplicitTTFTFromObj(child.attributes, child.startTime) ??
        extractExplicitTTFTFromObj(child.metadata, child.startTime);

      const childStart = child.startTime
        ? new Date(child.startTime).getTime()
        : null;
      const childLatency = typeof child.latencyMs === "number" ? child.latencyMs : null;

      // Effective child TTFT: explicit or 25% of child latency
      const effectiveChildTTFT = childTTFT ?? (childLatency !== null ? childLatency * 0.25 : null);

      if (effectiveChildTTFT !== null) {
        if (childStart && rootStartTime && childStart >= rootStartTime) {
          const rootToChildTTFT = (childStart - rootStartTime) + effectiveChildTTFT;
          if (rootToChildTTFT >= 0 && rootToChildTTFT < minDiff) {
            minDiff = rootToChildTTFT;
            earliestRootTTFT = rootToChildTTFT;
          }
        } else if (effectiveChildTTFT >= 0 && effectiveChildTTFT < minDiff) {
          minDiff = effectiveChildTTFT;
          earliestRootTTFT = effectiveChildTTFT;
        }
      }
    }

    if (earliestRootTTFT !== null && earliestRootTTFT !== Infinity) {
      return earliestRootTTFT;
    }
  }

  // 4. Fallback: If span has total latencyMs, calculate TTFT as 25% of latency
  if (typeof span.latencyMs === "number" && span.latencyMs > 0) {
    return span.latencyMs * 0.25;
  }

  return null;
}
